import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import { EmptyState } from "@/components/base/StateViews";
import { searchAll, type SearchResultItem } from "@/utils/data";
import { useSeo } from "@/hooks/useSeo";
import type { SearchCategory } from "@/types";

const tabs: { label: string; value: SearchCategory }[] = [
  { label: "全部", value: "all" },
  { label: "电影", value: "movie" },
  { label: "电视剧", value: "tv" },
  { label: "影评", value: "review" },
  { label: "资讯", value: "news" },
];

const categoryLabel: Record<string, string> = {
  movie: "电影",
  tv: "电视剧",
  review: "影评",
  news: "资讯",
};

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const [input, setInput] = useState(q);
  const [tab, setTab] = useState<SearchCategory>("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setInput(q);
  }, [q]);

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 260);
    return () => window.clearTimeout(timer);
  }, [q, tab]);

  useSeo({
    title: q ? `“${q}”的搜索结果 - 亚洲天堂` : "搜索 - 亚洲天堂",
    description: `在亚洲天堂搜索“${q}”相关的电影、电视剧、影评与资讯内容。`,
    keywords: `${q},影视搜索,亚洲天堂搜索,电影搜索,影评搜索`,
  });

  const results: SearchResultItem[] = useMemo(() => searchAll(q, tab), [q, tab]);

  const counts = useMemo(() => {
    const all = searchAll(q, "all");
    return {
      all: all.length,
      movie: all.filter((r) => r.category === "movie").length,
      tv: all.filter((r) => r.category === "tv").length,
      review: all.filter((r) => r.category === "review").length,
      news: all.filter((r) => r.category === "news").length,
    } as Record<SearchCategory, number>;
  }, [q]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const next = input.trim();
    if (next) setParams({ q: next });
    else setParams({});
  };

  return (
    <div className="animate-fade-in">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-site px-4 py-8 md:px-6 md:py-10">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">站内搜索</h1>
          <p className="mt-2 text-sm text-foreground-500">搜索电影、电视剧、影评与影视资讯</p>
          <form onSubmit={handleSubmit} className="mt-5 max-w-2xl">
            <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2 transition-colors focus-within:border-primary-400">
              <i className="ri-search-line text-lg text-foreground-400" aria-hidden="true"></i>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入关键词，如：悬疑、科幻、影评…"
                aria-label="搜索关键词"
                className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
              />
              <button
                type="submit"
                className="shrink-0 rounded-full bg-primary-500 px-4 py-1.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                搜索
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="mx-auto w-full max-w-site px-4 py-8 md:px-6 md:py-10">
        {q ? (
          <>
            <div className="mb-5 flex flex-wrap items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
              {tabs.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setTab(item.value)}
                  className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm transition-colors whitespace-nowrap ${
                    tab === item.value ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-primary-700"
                  }`}
                >
                  {item.label}
                  <span className={`text-xs ${tab === item.value ? "text-background-50/80" : "text-foreground-400"}`}>
                    {counts[item.value]}
                  </span>
                </button>
              ))}
            </div>

            <p className="mb-5 text-sm text-foreground-500">
              关键词“<strong className="text-foreground-800">{q}</strong>”共找到{" "}
              <strong className="text-accent-600">{results.length}</strong> 条结果
            </p>

            {loading ? (
              <div className="py-16 text-center text-sm text-foreground-400">正在搜索…</div>
            ) : results.length === 0 ? (
              <EmptyState
                title="没有找到相关内容"
                description={`未找到与“${q}”匹配的结果，换个关键词试试，或返回首页浏览推荐内容。`}
                icon="ri-search-eye-line"
              />
            ) : (
              <div className="flex flex-col gap-4">
                {results.map((item) => (
                  <Link
                    key={`${item.category}-${item.id}`}
                    to={item.link}
                    className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-all duration-300 hover:border-primary-200 sm:flex-row"
                  >
                    <span className="h-40 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-24 sm:w-36">
                      <img
                        src={item.image}
                        alt={item.title}
                        title={`${item.title} - 亚洲天堂`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </span>
                    <span className="flex flex-1 flex-col">
                      <span className="mb-1.5 flex flex-wrap items-center gap-2">
                        <Badge tone={item.category === "review" ? "accent" : item.category === "news" ? "secondary" : "primary"} className="text-[10px]">
                          {categoryLabel[item.category]}
                        </Badge>
                        <span className="text-xs text-foreground-400">{item.meta}</span>
                      </span>
                      <span className="mb-1 line-clamp-2 text-base font-semibold text-foreground-900 transition-colors group-hover:text-primary-700">
                        {item.title}
                      </span>
                      <span className="line-clamp-2 text-sm leading-relaxed text-foreground-500">{item.summary}</span>
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-2xl text-secondary-500">
              <i className="ri-search-line" aria-hidden="true"></i>
            </div>
            <p className="text-sm text-foreground-500">输入关键词开始搜索，试试“科幻”“悬疑”或“影评”</p>
            <div className="flex flex-wrap justify-center gap-2">
              {["科幻", "悬疑", "爱情", "影评", "古装"].map((word) => (
                <Link
                  key={word}
                  to={`/search?q=${encodeURIComponent(word)}`}
                  className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-sm text-foreground-600 transition-colors hover:border-accent-300 hover:text-accent-600"
                >
                  {word}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}